import React, { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ISelectOptionProps } from 'components/form/types';
import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import UpdateEntity from 'components/crud/UpdateEntity';
import { useModal } from 'store/modal';
import FormRowGroup from 'components/form/FormRowGroup';
import { Col, Row } from 'react-bootstrap';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { IToken, IInputs } from '../types';

interface IProps {
  tokensEntityName: string;
  tokens: IToken[];
  fields: ISelectOptionProps[];
  isEditMode?: boolean;
  templateName?: string;
  tokenIdKey: string;
  fieldIdKey: string;
  templateFilter: string;
}

type DefaultValues = Record<`${number}-field_id`, ISelectOptionProps> & Record<`${number}-is_required`, boolean>;

const EditForm: FC<IProps> = ({
  tokensEntityName,
  tokens,
  fields,
  isEditMode,
  templateName,
  tokenIdKey,
  fieldIdKey,
  templateFilter,
}: IProps) => {
  const { id } = useParams<{ id: string }>();
  const { showModal } = useModal();
  const { client } = useActiveClientContext();
  const history = useHistory();

  const defaultValues = tokens.reduce<DefaultValues>((obj, curr: IToken) => {
    return {
      ...obj,
      [`${curr.id}-field_id`]: fields.find(field => field.value === curr[fieldIdKey as keyof IToken]),
      [`${curr.id}-is_required`]: curr.is_required,
    };
  }, {});

  const methods = useForm({
    defaultValues,
  });

  return (
    <UpdateEntity
      entity={tokensEntityName}
      methods={methods}
      onError={() => {
        return Object.keys(defaultValues).filter((key: any) => key.includes('field_id'));
      }}
      onSuccess={() => {
        showModal({
          type: 'success',
          content: (
            <>
              {`${isEditMode ? 'Редактирахте' : 'Създадохте'} шаблон за документ `}
              <span className="font-weight-bold">{templateName}</span>.
            </>
          ),
        });
      }}
      queryKey={[buildClientEntity(tokensEntityName, client), { [templateFilter]: id }]}
      onSuccessPushToLink={buildClientUrl('/document-templates', client)}
    >
      {(update: any) => (
        <Form
          methods={methods}
          onSubmit={(data: IInputs) => {
            const groupedFields: { [key: string]: any } = {};
            Object.keys(data).forEach(key => {
              const tokenPropPair = key.split('-');
              const tokenId = tokenPropPair[0];
              const propName = tokenPropPair[1];

              groupedFields[tokenId] = {
                ...groupedFields[tokenId],
                [propName]: data[key],
              };
            });

            const formattedData = Object.keys(groupedFields).map((key: string) => ({
              [tokenIdKey]: parseInt(key, 10),
              [fieldIdKey]: groupedFields[key].field_id.value,
              is_required: groupedFields[key].is_required,
            }));

            return update({ tokens: formattedData });
          }}
          btnLabel="Запази"
          onCancel={() => history.push(buildClientUrl('/document-templates', client))}
          showBackBtn
          onGoBack={() => history.push(buildClientUrl(`/document-templates/edit/${id}`, client))}
          isHorizontal
        >
          <Row className="d-none d-sm-flex">
            <Col sm={{ offset: 2, span: 6 }}>
              <Row>
                <Col>
                  <h6>Поле</h6>
                </Col>
                <Col>
                  <h6>Задължително</h6>
                </Col>
              </Row>
            </Col>
          </Row>
          {tokens.map(token => (
            <FormRowGroup
              key={token.id}
              label={token.token}
              isRequired
            >
              <FormGroup
                controlId={`formGroup_${token.id}_field_id`}
                name={`${token.id}-field_id`}
                type="select"
                as="select"
                isRequired
                selectOptions={fields}
                isRowGroup
              />
              <FormGroup
                controlId={`formGroup_${token.id}_is_required`}
                checkType="switch"
                name={`${token.id}-is_required`}
                isRowGroup
              />
            </FormRowGroup>
          ))}
        </Form>
      )}
    </UpdateEntity>
  );
};

export default EditForm;
